import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';

interface DriveItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  size?: number;
  lastModified?: string;
  webUrl?: string;
  clientRef?: string;
  childCount?: number;
}

interface Props {
  baseApiUrl: string;
  folderId?: string;
  onNavigate?: (id: string, name: string) => void;
  onSelectDocument?: (item: DriveItem) => void;
  selectedItemId?: string;
  multiSelect?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  searchFilter?: string;
  onItemsLoaded?: (items: DriveItem[]) => void;
  unindexedIds?: Set<string>;
}

type SortKey = 'name' | 'items' | 'size' | 'modified';
type SortDir = 'asc' | 'desc';
type TypeFilter = 'all' | 'folder' | 'file';

function NotIndexedIcon() {
  return (
    <svg
      className="w-3.5 h-3.5 text-amber-500 inline ml-1 shrink-0"
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-label="Not yet indexed — will be indexed before generating"
    >
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg className="w-4 h-4 text-yellow-500 inline" fill="currentColor" viewBox="0 0 20 20">
      <path d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
    </svg>
  );
}

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

function PdfIcon() {
  return (
    <svg className="w-4 h-4 text-red-500 inline" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" className="text-xs font-bold fill-white" fontSize="6" fontWeight="bold">PDF</text>
    </svg>
  );
}

function ExcelIcon() {
  return (
    <svg className="w-4 h-4 text-green-600 inline" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" className="text-xs font-bold fill-white" fontSize="6" fontWeight="bold">XLS</text>
    </svg>
  );
}

function WordIcon() {
  return (
    <svg className="w-4 h-4 text-blue-600 inline" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" className="text-xs font-bold fill-white" fontSize="6" fontWeight="bold">DOC</text>
    </svg>
  );
}

function CsvIcon() {
  return (
    <svg className="w-4 h-4 text-cyan-600 inline" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" className="text-xs font-bold fill-white" fontSize="6" fontWeight="bold">CSV</text>
    </svg>
  );
}

function PowerPointIcon() {
  return (
    <svg className="w-4 h-4 text-orange-600 inline" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" className="text-xs font-bold fill-white" fontSize="6" fontWeight="bold">PPT</text>
    </svg>
  );
}

function GenericFileIcon() {
  return (
    <svg className="w-4 h-4 text-gray-400 inline" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
    </svg>
  );
}

function FileIcon({ filename }: { filename: string }) {
  const ext = getFileExtension(filename);
  
  switch (ext) {
    case 'pdf':
      return <PdfIcon />;
    case 'xls':
    case 'xlsx':
    case 'xlsm':
    case 'csv':
      return ext === 'csv' ? <CsvIcon /> : <ExcelIcon />;
    case 'doc':
    case 'docx':
    case 'docm':
      return <WordIcon />;
    case 'ppt':
    case 'pptx':
    case 'pptm':
      return <PowerPointIcon />;
    default:
      return <GenericFileIcon />;
  }
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) {
    return (
      <svg className="w-3.5 h-3.5 text-gray-400 ml-1 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  return dir === 'asc' ? (
    <svg className="w-3.5 h-3.5 text-blue-600 ml-1 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-3.5 h-3.5 text-blue-600 ml-1 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function formatBytes(bytes: number | undefined): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function DocumentList({
  baseApiUrl, folderId = 'root', onNavigate, onSelectDocument, selectedItemId,
  multiSelect = false, selectedIds, onSelectionChange, searchFilter,
  onItemsLoaded, unindexedIds,
}: Props) {
  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, DriveItem[]>>({});
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [typeFilter] = useState<TypeFilter>('all');

  const selectAllRef = useRef<HTMLInputElement>(null);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const isChecked = (id: string) => Array.isArray(selectedIds) && selectedIds.includes(id);
  function toggleChecked(id: string) {
    if (!onSelectionChange) return;
    const current = Array.isArray(selectedIds) ? selectedIds : [];
    onSelectionChange(current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  }

  const processedItems = useMemo(() => {
    let result = searchFilter
      ? items.filter((i) => i.name.toLowerCase().includes(searchFilter.toLowerCase()))
      : [...items];

    if (typeFilter !== 'all') {
      result = result.filter((i) => i.type === typeFilter);
    }

    result.sort((a, b) => {
      // Folders always before files, then sort within each group
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;

      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
          break;
        case 'items':
          cmp = (a.childCount ?? 0) - (b.childCount ?? 0);
          break;
        case 'size':
          cmp = (a.size ?? 0) - (b.size ?? 0);
          break;
        case 'modified':
          cmp = new Date(a.lastModified ?? 0).getTime() - new Date(b.lastModified ?? 0).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [items, searchFilter, typeFilter, sortKey, sortDir]);

  const allSelectableIds = useMemo(() => {
    const ids: string[] = [];
    processedItems.filter((i) => i.type === 'file').forEach((i) => ids.push(i.id));
    Object.values(expanded).forEach((children) =>
      children.filter((c) => c.type === 'file').forEach((c) => ids.push(c.id))
    );
    return ids;
  }, [processedItems, expanded]);

  const allSelected = allSelectableIds.length > 0 && allSelectableIds.every((id) => isChecked(id));
  const someSelected = !allSelected && allSelectableIds.some((id) => isChecked(id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  function toggleSelectAll() {
    if (!onSelectionChange) return;
    const current = Array.isArray(selectedIds) ? selectedIds : [];
    if (allSelected) {
      onSelectionChange(current.filter((id) => !allSelectableIds.includes(id)));
    } else {
      const merged = Array.from(new Set([...current, ...allSelectableIds]));
      onSelectionChange(merged);
    }
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get<DriveItem[]>(`${baseApiUrl}/api/documents/folder/${folderId}`)
      .then((res) => {
        const loaded = Array.isArray(res.data) ? res.data : [];
        setItems(loaded);
        onItemsLoaded?.(loaded);
      })
      .catch((err: { response?: { data?: { error?: string } }; message: string }) =>
        setError(err.response?.data?.error ?? err.message)
      )
      .finally(() => setLoading(false));
  }, [baseApiUrl, folderId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleDownload(itemId: string, name: string) {
    const a = document.createElement('a');
    a.href = `${baseApiUrl}/api/documents/download/${itemId}`;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async function handleExpandFolder(item: DriveItem) {
    if (expanded[item.id]) {
      const next = { ...expanded };
      delete next[item.id];
      setExpanded(next);
      return;
    }
    try {
      const res = await axios.get<DriveItem[]>(`${baseApiUrl}/api/documents/folder/${item.id}`);
      setExpanded((prev) => ({ ...prev, [item.id]: res.data }));
      onItemsLoaded?.(res.data);
    } catch (err: unknown) {
      const e = err as { message: string };
      console.error('Failed to expand folder:', e.message);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading...
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 py-4">Error: {error}</div>;
  }

  if (items.length === 0) {
    return <div className="text-gray-500 py-4">This folder is empty.</div>;
  }

  if (processedItems.length === 0) {
    return <div className="text-gray-500 py-4 px-4">No results{searchFilter ? ` for "${searchFilter}"` : ''}.</div>;
  }

  function ColHeader({ label, col, className }: { label: string; col: SortKey; className?: string }) {
    const active = sortKey === col;
    return (
      <th className={`text-left px-4 py-3 ${className ?? ''}`}>
        <button
          onClick={() => handleSort(col)}
          className={`inline-flex items-center font-medium hover:text-gray-900 transition-colors ${active ? 'text-blue-600' : 'text-gray-600'}`}
        >
          {label}
          <SortIcon active={active} dir={sortDir} />
        </button>
      </th>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">

      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
          <tr>
            {multiSelect && (
              <th className="w-10 px-3 text-center">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  disabled={allSelectableIds.length === 0}
                  className="w-4 h-4 cursor-pointer accent-blue-600"
                  title={allSelected ? 'Deselect all' : 'Select all'}
                />
              </th>
            )}
            <th className="w-10 px-2" />
            <ColHeader label="Name" col="name" />
            <ColHeader label="Items" col="items" className="w-20" />
            <ColHeader label="Size" col="size" className="w-24" />
            <ColHeader label="Modified" col="modified" className="w-44" />
            <th className="text-left px-4 py-3 font-medium text-gray-600 w-28">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {processedItems.map((item) => (
            <React.Fragment key={item.id}>
              <tr
                className={`transition-colors ${
                  (multiSelect && item.type === 'file' && isChecked(item.id))
                    ? 'bg-blue-50 ring-1 ring-inset ring-blue-200'
                    : selectedItemId === item.id
                      ? 'bg-blue-50 ring-1 ring-inset ring-blue-200'
                      : 'hover:bg-gray-50'
                }`}
              >
                {multiSelect && (
                  <td className="px-3 py-3 align-middle">
                    {item.type === 'file' ? (
                      <input
                        type="checkbox"
                        checked={isChecked(item.id)}
                        onChange={() => toggleChecked(item.id)}
                        className="w-4 h-4 cursor-pointer accent-blue-600"
                      />
                    ) : null}
                  </td>
                )}
                <td className="px-2 py-3 text-center align-middle w-10">
                  {item.type === 'folder' ? <FolderIcon /> : <FileIcon filename={item.name} />}
                </td>
                <td className="px-4 py-3">
                  {item.type === 'folder' ? (
                    <button
                      onClick={() => onNavigate ? onNavigate(item.id, item.name) : handleExpandFolder(item)}
                      className="text-blue-600 hover:underline text-left"
                    >
                      {item.name}
                    </button>
                  ) : multiSelect ? (
                    <button
                      onClick={() => toggleChecked(item.id)}
                      className="text-left text-gray-800 hover:text-blue-700 w-full inline-flex items-center"
                    >
                      <span>{item.name}</span>
                      {unindexedIds?.has(item.id) && <NotIndexedIcon />}
                    </button>
                  ) : (
                    <button
                      onClick={() => onSelectDocument && onSelectDocument(item)}
                      className="text-left text-gray-800 hover:text-blue-700 w-full inline-flex items-center"
                    >
                      <span>{item.name}</span>
                      {unindexedIds?.has(item.id) && <NotIndexedIcon />}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-500">{item.type === 'folder' ? (item.childCount ?? '—') : '—'}</td>
                <td className="px-4 py-3 text-gray-500">{item.type === 'folder' ? '—' : formatBytes(item.size)}</td>
                <td className="px-4 py-3 text-gray-500">{formatDate(item.lastModified)}</td>
                <td className="px-4 py-3">
                  {item.type === 'file' && (
                    <div className="flex gap-3">
                      {item.webUrl && (
                        <a
                          href={item.webUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline text-xs"
                        >
                          Open
                        </a>
                      )}
                      <button
                        onClick={() => handleDownload(item.id, item.name)}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        Download
                      </button>
                    </div>
                  )}
                  {item.type === 'folder' && onNavigate && (
                    <button
                      onClick={() => onNavigate(item.id, item.name)}
                      className="text-gray-500 hover:underline text-xs"
                    >
                      Open
                    </button>
                  )}
                </td>
              </tr>
              {expanded[item.id]?.map((child) => (
                <tr key={child.id} className="bg-blue-50 hover:bg-blue-100 transition-colors">
                  {multiSelect && (
                    <td className="px-3 py-2 align-middle">
                      {child.type === 'file' ? (
                        <input
                          type="checkbox"
                          checked={isChecked(child.id)}
                          onChange={() => toggleChecked(child.id)}
                          className="w-4 h-4 cursor-pointer accent-blue-600"
                        />
                      ) : null}
                    </td>
                  )}
                  <td className="px-2 py-2 text-center align-middle w-10 pl-8">
                    {child.type === 'folder' ? <FolderIcon /> : <FileIcon filename={child.name} />}
                  </td>
                  <td className="px-4 py-2 inline-flex items-center">
                    <span className="text-gray-700">{child.name}</span>
                    {child.type === 'file' && unindexedIds?.has(child.id) && <NotIndexedIcon />}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{child.type === 'folder' ? (child.childCount ?? '—') : '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{child.type === 'folder' ? '—' : formatBytes(child.size)}</td>
                  <td className="px-4 py-2 text-gray-500">{formatDate(child.lastModified)}</td>
                  <td className="px-4 py-2">
                    {child.type === 'file' && (
                      <div className="flex gap-3">
                        {child.webUrl && (
                          <a
                            href={child.webUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline text-xs"
                          >
                            Open
                          </a>
                        )}
                        <button
                          onClick={() => handleDownload(child.id, child.name)}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          Download
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
